import { useState } from "react";
import { ArrowRight, Lock } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";

const DONATION_LINKS = {
  once: { 10: "", 25: "", 50: "", 100: "" },
  monthly: { 10: "", 25: "", 50: "", 100: "" },
};

export default function Donar() {
  const { t } = useLang();
  const [freq, setFreq] = useState("once");
  const [amount, setAmount] = useState(25);

  const give = () => {
    const url = DONATION_LINKS[freq][amount];
    if (!url) {
      toast.info(t.donar.comingSoon);
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="donar-page">
      <section className="pt-40 pb-20 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>{t.donar.overline}</Overline>
          <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight mt-6 max-w-3xl">
            {t.donar.title}
          </h1>
          <p className="text-white/60 text-lg mt-8 max-w-xl leading-relaxed">{t.donar.sub}</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Donation widget */}
        <Reveal className="lg:col-span-7">
          <div className="border border-white/10 bg-coal p-10 md:p-14" data-testid="donation-widget">
            <div className="flex gap-3 mb-12">
              {["once", "monthly"].map((f) => (
                <button
                  key={f}
                  data-testid={`donate-freq-${f}`}
                  onClick={() => setFreq(f)}
                  className={`text-xs uppercase tracking-[0.2em] rounded-full px-6 py-3 border transition-colors duration-300 ${
                    freq === f
                      ? "bg-ember border-ember text-white"
                      : "border-white/20 text-white/60 hover:border-white/50"
                  }`}
                >
                  {t.donar[f]}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
              {t.donar.amounts.map((a) => (
                <button
                  key={a}
                  data-testid={`donate-amount-${a}`}
                  onClick={() => setAmount(a)}
                  className={`font-display text-4xl font-light py-8 border transition-colors duration-300 ${
                    amount === a
                      ? "border-ember bg-ember/10 text-sand"
                      : "border-white/15 text-white/60 hover:border-white/40"
                  }`}
                >
                  ${a}
                </button>
              ))}
            </div>

            <button
              data-testid="donate-submit-btn"
              onClick={give}
              className="group w-full flex items-center justify-center gap-3 bg-ember text-black text-xs uppercase tracking-[0.2em] font-bold rounded-full px-10 py-5 hover:bg-white transition-colors duration-300"
            >
              {`${t.donar.submit} · $${amount}${freq === "monthly" ? "/mo" : ""}`}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <p className="flex items-center gap-2 text-xs text-white/40 mt-8">
              <Lock size={12} className="text-ember" />
              {t.donar.secure}
            </p>
          </div>
        </Reveal>

        {/* Impact */}
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <h2 className="font-display text-3xl md:text-4xl font-light mb-12">{t.donar.impactTitle}</h2>
            <div className="flex flex-col gap-10">
              {t.donar.impact.map((item, i) => (
                <div key={i} className="border-t border-white/15 pt-8" data-testid={`impact-${i}`}>
                  <span className="font-display text-3xl font-light text-ember">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="text-lg font-medium mt-4 mb-2">{item.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
